import { Paper, styled } from "@mui/material";
import { useMemo } from "react";

import { BoldSpan } from "@/Components/v2/atoms/Typography";
import { useInfo } from "@/context/InfoProvider/InfoProvider";

interface NodeHierarchyBreadcrumbProps {
  nodeUUID: string;
  rootLevelLabel?: string | null;
}

const HIERARCHY_SEPARATOR = ">";

// Add styled component for the custom alert
const StyledPaper = styled(Paper)(({ theme }) => ({
  position: "relative",
  padding: theme.spacing(2),
  backgroundColor: theme.palette.tooltip.background,
  display: "inline-flex",
  gap: theme.spacing(1),
  width: "fit-content",
  "&::before": {
    content: '""',
    position: "absolute",
    top: -8,
    left: 24,
    width: 0,
    height: 0,
    borderLeft: "8px solid transparent",
    borderRight: "8px solid transparent",
    borderBottom: `8px solid ${theme.palette.tooltip.background}`,
  },
}));

export const NodeHierarchyBreadcrumb: React.FC<NodeHierarchyBreadcrumbProps> = ({
  nodeUUID,
  rootLevelLabel = null,
}) => {
  const { nodes, loading } = useInfo();

  const selectedNodeHierarchy = useMemo(() => {
    if (!nodeUUID || !nodes) return null;

    const ancestors = nodes.getNodeAndAncestors(nodeUUID);
    if (ancestors.length === 0) return null;

    if (rootLevelLabel !== null) {
      const rootNode = ancestors[ancestors.length - 1];
      return `${rootNode!.warchestNode.extras.mappedName || rootNode!.warchestNode.name} ${HIERARCHY_SEPARATOR} ${rootLevelLabel}`;
    }

    const ancestorNames = ancestors
      .reverse()
      .map((node) => node.warchestNode.extras.mappedName || node.warchestNode.name);

    if (ancestorNames.length === 1) {
      return <BoldSpan>{ancestorNames[0]}</BoldSpan>;
    }

    const lastItem = ancestorNames.pop();
    return (
      <>
        {ancestorNames.join(` ${HIERARCHY_SEPARATOR} `)}
        {` ${HIERARCHY_SEPARATOR} `}
        <BoldSpan>{lastItem}</BoldSpan>
      </>
    );
  }, [nodeUUID, nodes, rootLevelLabel]);

  if (loading) return null;

  return (
    <StyledPaper elevation={0}>
      <BoldSpan>Showing: </BoldSpan> {selectedNodeHierarchy}
    </StyledPaper>
  );
};

export default NodeHierarchyBreadcrumb;
